/**
 * Invoice PDF Service
 * Generates professional invoice PDFs using jsPDF.
 * Follows the same patterns as payroll-pdf-service.ts.
 */

import jsPDF from 'jspdf';
import { Invoice } from '../types/accounting';
import { Business } from '../types/accounting';

export class InvoicePDFService {
  private readonly MARGIN = 25.4; // 1 inch in mm
  private readonly PAGE_WIDTH = 210; // A4 width
  private readonly PAGE_HEIGHT = 297; // A4 height

  /** Red header bar with branding */
  private addHeader(pdf: jsPDF, businessName: string): number {
    const margin = this.MARGIN;
    const headerHeight = 20;

    // Red background
    pdf.setFillColor(220, 38, 38); // #dc2626
    pdf.rect(0, 0, this.PAGE_WIDTH, headerHeight, 'F');

    // Company name
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('times', 'bold');
    pdf.text(businessName || "Larry's Help", margin, 12);

    // Website
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.text('LarrysHelp.org', this.PAGE_WIDTH - margin, 12, { align: 'right' });

    // Reset
    pdf.setTextColor(0, 0, 0);

    return headerHeight + 8;
  }

  /** Footer */
  private addFooter(pdf: jsPDF): void {
    const margin = this.MARGIN;
    const y = this.PAGE_HEIGHT - margin + 5;

    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, this.PAGE_WIDTH - margin, y);

    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text("Generated by Larry's Help", margin, y + 5);
    pdf.text('Page 1 of 1', this.PAGE_WIDTH - margin, y + 5, { align: 'right' });
  }

  /** Generate and download invoice PDF */
  generateInvoicePDF(
    invoice: Invoice,
    business: Business | null,
    formatCurrency: (amount: number) => string
  ): void {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const margin = this.MARGIN;
    const contentWidth = this.PAGE_WIDTH - 2 * margin;
    const rightCol = this.PAGE_WIDTH - margin;

    // Header
    let y = this.addHeader(pdf, business?.business_name || '');

    // INVOICE title
    pdf.setFontSize(22);
    pdf.setFont('times', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('INVOICE', rightCol, y + 5, { align: 'right' });
    y += 12;

    // Invoice details (right side)
    pdf.setFontSize(10);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(60, 60, 60);

    const detailsStartY = y;
    pdf.text('Invoice #: ' + invoice.invoice_number, rightCol, y, { align: 'right' });
    y += 5;
    pdf.text('Date: ' + invoice.issue_date, rightCol, y, { align: 'right' });
    y += 5;
    pdf.text('Due: ' + invoice.due_date, rightCol, y, { align: 'right' });
    y += 5;

    if (invoice.paid_date) {
      pdf.setTextColor(5, 150, 105); // green
      pdf.text('Paid: ' + invoice.paid_date, rightCol, y, { align: 'right' });
      y += 5;
    }

    // Status badge
    const statusLabel = invoice.status.toUpperCase();
    pdf.setFontSize(9);
    if (invoice.status === 'paid') {
      pdf.setTextColor(5, 150, 105);
    } else if (invoice.status === 'overdue') {
      pdf.setTextColor(220, 38, 38);
    } else if (invoice.status === 'void') {
      pdf.setTextColor(156, 163, 175);
    } else {
      pdf.setTextColor(29, 78, 216);
    }
    pdf.setFont('times', 'bold');
    pdf.text('Status: ' + statusLabel, rightCol, y, { align: 'right' });
    y += 5;

    // Business info (left side - from line)
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont('times', 'bold');
    let leftY = detailsStartY;

    pdf.text('From:', margin, leftY);
    leftY += 5;
    pdf.setFont('times', 'normal');

    if (business) {
      pdf.text(business.business_name, margin, leftY);
      leftY += 4.5;
      if (business.address) {
        pdf.text(business.address, margin, leftY);
        leftY += 4.5;
      }
      const cityLine: string[] = [];
      if (business.city) cityLine.push(business.city);
      if (business.state_province) cityLine.push(business.state_province);
      if (business.postal_code) cityLine.push(business.postal_code);
      if (cityLine.length > 0) {
        pdf.text(cityLine.join(', '), margin, leftY);
        leftY += 4.5;
      }
      if (business.email) {
        pdf.text(business.email, margin, leftY);
        leftY += 4.5;
      }
      if (business.phone) {
        pdf.text(business.phone, margin, leftY);
        leftY += 4.5;
      }
      if (business.tax_registration_number) {
        // A registrant's number has to be on the invoice for the customer to claim the tax back
        const taxLabel = (business.country || '').toLowerCase().startsWith('can') ? 'GST/HST No.' : 'Tax ID';
        pdf.text(`${taxLabel} ${business.tax_registration_number}`, margin, leftY);
        leftY += 4.5;
      }
    }

    // Bill To section
    y = Math.max(y, leftY) + 8;

    pdf.setFont('times', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Bill To:', margin, y);
    y += 5;
    pdf.setFont('times', 'normal');
    pdf.text(invoice.contact_display_name, margin, y);
    y += 8;

    // Divider line
    pdf.setDrawColor(220, 38, 38);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, this.PAGE_WIDTH - margin, y);
    y += 8;

    // Line Items Table
    const colDesc = margin;
    const colQty = margin + contentWidth * 0.50;
    const colPrice = margin + contentWidth * 0.62;
    const colTax = margin + contentWidth * 0.77;
    const colAmount = rightCol;

    // Table header
    pdf.setFillColor(249, 250, 251); // #f9fafb
    pdf.rect(margin, y - 4, contentWidth, 8, 'F');

    pdf.setFontSize(9);
    pdf.setFont('times', 'bold');
    pdf.setTextColor(55, 65, 81);
    pdf.text('Description', colDesc, y);
    pdf.text('Qty', colQty, y, { align: 'right' });
    pdf.text('Unit Price', colPrice, y, { align: 'right' });
    pdf.text('Tax', colTax, y, { align: 'right' });
    pdf.text('Amount', colAmount, y, { align: 'right' });
    y += 6;

    // Table rows
    pdf.setFont('times', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(9);

    const lines = invoice.lines || [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check for page overflow
      if (y > this.PAGE_HEIGHT - 60) {
        this.addFooter(pdf);
        pdf.addPage();
        y = 20;
      }

      // Alternate row shading
      if (i % 2 === 1) {
        pdf.setFillColor(249, 250, 251);
        pdf.rect(margin, y - 3.5, contentWidth, 6, 'F');
      }

      // Truncate long descriptions
      const desc = line.description.length > 55
        ? line.description.substring(0, 52) + '...'
        : line.description;

      pdf.text(desc, colDesc, y);
      pdf.text(String(line.quantity), colQty, y, { align: 'right' });
      pdf.text(formatCurrency(line.unit_price), colPrice, y, { align: 'right' });
      pdf.text(line.tax_amount > 0 ? formatCurrency(line.tax_amount) : '-', colTax, y, { align: 'right' });
      pdf.text(formatCurrency(line.amount), colAmount, y, { align: 'right' });
      y += 6;
    }

    // Bottom border of table
    y += 2;
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, this.PAGE_WIDTH - margin, y);
    y += 8;

    // Totals (right-aligned block)
    const totalsLabelX = margin + contentWidth * 0.65;

    pdf.setFontSize(10);
    pdf.setFont('times', 'normal');
    pdf.text('Subtotal:', totalsLabelX, y);
    pdf.text(formatCurrency(invoice.subtotal), rightCol, y, { align: 'right' });
    y += 6;

    if (invoice.tax_amount > 0) {
      pdf.text('Tax:', totalsLabelX, y);
      pdf.text(formatCurrency(invoice.tax_amount), rightCol, y, { align: 'right' });
      y += 6;
    }

    // Total with emphasis
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(totalsLabelX, y - 1, rightCol, y - 1);
    y += 3;

    pdf.setFontSize(12);
    pdf.setFont('times', 'bold');
    pdf.text('Total:', totalsLabelX, y);
    pdf.text(formatCurrency(invoice.total), rightCol, y, { align: 'right' });
    y += 3;
    pdf.setLineWidth(0.5);
    pdf.line(totalsLabelX, y, rightCol, y);
    y += 6;

    // Part-paid invoices have to show what is left owing
    const amountPaid = invoice.amount_paid || 0;
    if (amountPaid > 0 && invoice.status !== 'paid') {
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.text('Paid to date:', totalsLabelX, y);
      pdf.text(formatCurrency(amountPaid), rightCol, y, { align: 'right' });
      y += 5;
      pdf.setFont('times', 'bold');
      pdf.text('Balance due:', totalsLabelX, y);
      pdf.text(formatCurrency(invoice.total - amountPaid), rightCol, y, { align: 'right' });
      y += 6;
    }
    y += 6;

    // Notes
    if (invoice.notes) {
      pdf.setFontSize(10);
      pdf.setFont('times', 'bold');
      pdf.text('Notes:', margin, y);
      y += 5;
      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(75, 85, 99);

      const noteLines = pdf.splitTextToSize(invoice.notes, contentWidth);
      pdf.text(noteLines, margin, y);
      y += noteLines.length * 4.5 + 4;
    }

    // Terms
    if (invoice.terms) {
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('times', 'bold');
      pdf.text('Terms:', margin, y);
      y += 5;
      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(75, 85, 99);

      const termLines = pdf.splitTextToSize(invoice.terms, contentWidth);
      pdf.text(termLines, margin, y);
    }

    // Footer
    this.addFooter(pdf);

    // Download
    const filename = invoice.invoice_number + '.pdf';
    pdf.save(filename);
  }
}
